import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { API, api, formatApiError } from "@/lib/api";
import { PostImageButton } from "@/components/ContentPlanBar";

interface PinInfo { headlines: string[]; defaults: string[]; scheduled: number; posted: number }

export function PhotoWarning({ slug, onDone }: { slug: string; onDone: () => void }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-[#FDE6DF] px-4 py-3 text-sm font-semibold text-[#23324A]" data-testid={`photo-warning-${slug}`}>
      <AlertTriangle className="h-4 w-4 text-[#F08A6C]" strokeWidth={2.5} />
      <span className="flex-1">No photo yet. It will use the section's stock photo, and its pins will look like every other post in the section.</span>
      <span className="flex items-center gap-1">Add photo <PostImageButton slug={slug} onDone={onDone} /></span>
    </div>
  );
}

export function PinPreview({ slug, version }: { slug: string; version: number }) {
  const [info, setInfo] = useState<PinInfo | null>(null);
  const [heads, setHeads] = useState(["", "", ""]);
  const [shown, setShown] = useState(["", "", ""]);
  const [stamp, setStamp] = useState(Date.now());
  useEffect(() => {
    api.get(`/admin/posts/${slug}/pins`).then((r) => { setInfo(r.data); setHeads(r.data.headlines); setShown(r.data.headlines); }).catch((e) => toast.error(formatApiError(e)));
  }, [slug]);
  useEffect(() => { setStamp(Date.now()); }, [version]);
  if (!info) return null;
  const dirty = heads.some((h, i) => h !== info.headlines[i]);
  const save = async () => {
    try {
      await api.patch(`/admin/posts/${slug}`, { pin_headlines: heads });
      setInfo({ ...info, headlines: heads });
      setShown(heads);
      setStamp(Date.now());
      toast.success(info.scheduled ? "Pin headlines saved; scheduled pins redrawn" : "Pin headlines saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const src = (i: number) => `${API}/admin/posts/${slug}/pin-preview/${i}?headline=${encodeURIComponent(shown[i])}&t=${stamp}`;
  return (
    <section className="mt-6 rounded-2xl border-2 border-[#23324A]/15 bg-white p-4" data-testid={`pin-preview-${slug}`}>
      <p className="font-display text-base font-black text-[#23324A]">Pinterest pins</p>
      <p className="text-xs text-[#23324A]/65">{info.posted ? `${info.posted} posted · ` : ""}{info.scheduled ? `${info.scheduled} scheduled` : "Scheduled automatically when you publish"}. Leave a headline empty to use the default.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="min-w-0">
            <img src={src(i)} alt={`Pin design ${i + 1}`} className="aspect-[2/3] w-full rounded-xl border-2 border-[#23324A]/20 object-cover" loading="lazy" data-testid={`pin-preview-img-${i}`} />
            <input className="field mt-2 text-sm" maxLength={90} value={heads[i]} placeholder={info.defaults[i]}
              onChange={(e) => setHeads(heads.map((h, j) => (j === i ? e.target.value : h)))} data-testid={`pin-headline-input-${i}`} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button className="btn-outline" disabled={!heads.some((h, i) => h !== shown[i])} onClick={() => setShown(heads)} data-testid="pin-preview-update-btn"><RefreshCw className="h-4 w-4" />Update previews</button>
        <button className="btn-navy" disabled={!dirty} onClick={save} data-testid="pin-headlines-save-btn">Save headlines</button>
      </div>
    </section>
  );
}
